import { Injectable, inject } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AppStateService } from '../services/app-state.service';
import { AppInitState, NeedLoginError } from '@app/models/app-init-state.model';

/**
 * Authentication Guard
 *
 * Protects routes that require authentication.
 * Uses AppStateService to ensure the state machine has completed
 * before checking authentication status.
 *
 * Flow:
 * 1. Call AppStateService.initialize()
 * 2. If NeedLoginError is thrown, login redirect is already triggered by AppStateService
 * 3. If Ready state is reached, check authentication and allow access
 * 4. If Error state, redirect to unauthorized page
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private appStateService = inject(AppStateService);
  private router = inject(Router);

  async canActivate(): Promise<boolean> {
    console.log('🔒 [AUTH GUARD] canActivate() called');

    const currentState = this.appStateService.currentState();
    console.log('🔒 [AUTH GUARD] Current app state:', currentState);

    // If not ready, trigger initialization
    if (currentState !== AppInitState.Ready) {
      console.log('🔒 [AUTH GUARD] App not ready, triggering initialization...');

      try {
        await this.appStateService.initialize();
        console.log('🔒 [AUTH GUARD] App initialization completed successfully');
      } catch (error: any) {
        // NeedLoginError means AppStateService is triggering login redirect
        // We just need to return false and let the redirect happen
        if (error instanceof NeedLoginError || error?.name === 'NeedLoginError') {
          console.log('🔒 [AUTH GUARD] Login redirect triggered by AppStateService');

          // Store the intended path for redirect after login (if not already stored)
          const existingPath = sessionStorage.getItem('postLoginRedirectPath');
          if (!existingPath) {
            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            console.log('🔒 [AUTH GUARD] Storing redirect path:', currentPath);
            sessionStorage.setItem('postLoginRedirectPath', currentPath);
          }

          // Return false - browser will redirect to B2C
          return false;
        }

        // Actual error occurred
        console.error('🔒 [AUTH GUARD] ❌ App initialization failed:', error);
        this.router.navigate(['/unauthorized'], { queryParams: { reason: 'init_failed' } });
        return false;
      }
    }

    // App is ready - verify authentication
    const isAuthenticated = this.authService.isAuthenticated();
    console.log('🔒 [AUTH GUARD] isAuthenticated:', isAuthenticated);

    if (!isAuthenticated) {
      // This shouldn't happen if AppStateService is working correctly
      // But handle it just in case
      console.log('🔒 [AUTH GUARD] User not authenticated after Ready state - triggering retry');
      try {
        await this.appStateService.retry();
      } catch (error) {
        // Login redirect should have been triggered
        return false;
      }
      return false;
    }

    const user = this.authService.user();
    console.log('🔒 [AUTH GUARD] ✅ User authenticated:', user?.email);
    return true;
  }
}
